import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStudentStore } from "../hooks/use-student-store";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader } from "../components/ui/card";
import { Loader2 } from "lucide-react";

const StudentRegistration = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { preLesson, registerStudent, loading, error } = useStudentStore();
  const [firstName, setFirstName] = useState("");
  const [lastNameInitial, setLastNameInitial] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  if (loading && !preLesson) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error && !preLesson) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <p className="text-gray-500 mt-2">Please check the pre-lesson code and try again.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!firstName.trim()) {
      setLocalError("Please enter your first name");
      return;
    }
    if (!lastNameInitial.trim()) {
      setLocalError("Please enter your last name initial");
      return;
    }
    if (lastNameInitial.length !== 1) {
      setLocalError("Last name initial must be a single letter");
      return;
    }
    if (!/^[a-zA-Z]$/.test(lastNameInitial)) {
      setLocalError("Last name initial must be a letter");
      return;
    }

    try {
      const submissionId = await registerStudent(firstName.trim(), lastNameInitial.toUpperCase());
      navigate(`/student/${code}/${submissionId}`, { replace: true });
    } catch {
      setLocalError("Failed to register. Please try again.");
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {preLesson && (
            <CardDescription className="text-lg text-gray-900 mt-2 text-center">
              {preLesson.title}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastNameInitial">Last Name Initial</Label>
              <Input
                id="lastNameInitial"
                value={lastNameInitial}
                onChange={(e) => setLastNameInitial(e.target.value.slice(0, 1))}
                placeholder="e.g., S"
                maxLength={1}
                className="w-20"
                disabled={loading}
              />
            </div>

            {(localError || error) && (
              <p className="text-sm text-red-600">{localError || error}</p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Joining...
                </>
              ) : (
                "Start Pre-Lesson"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentRegistration;
